import { useTranslation } from "react-i18next";
import CustomInput from "../../custom-components/form-inputs/CustomInput";
import useCustomFormik from "../../custom-hooks/useCustomFormik";
import { Fields } from "../../types/SchemaFactory";
import { schemaFactory } from "../../validation-schemas/SchemaFactory";
import BaseModal from "../modals/BaseModal";
import { useState } from "react";
import FilterIconSVG from "./svg/FilterIconSVG";
import CustomToggle from "../../custom-components/form-inputs/CustomToggle";

interface GenericFiltersFormProps<T> {
    fieldsMeta: Fields<T>;
    schema: ReturnType<typeof schemaFactory>;
    filters: T;
    onSubmit: (values: Partial<T>) => void;
}

export function GenericFiltersForm<T>({ fieldsMeta, schema, onSubmit, filters }: GenericFiltersFormProps<T>) {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);

    const { form } = useCustomFormik<Partial<T>>({
        initial: null,
        fields: fieldsMeta,
        schema,
        onSubmit: (values) => onSubmit(values),
    });

    const confirmHandler = () => {
        setIsVisible(false);
        form.handleSubmit();
    };
    const closeHandler = () => {
        form.resetForm();
    };
    const onOpenHandler = () => {
        form.setValues(filters);
    };

    return (
        <BaseModal
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            title={t("console.common.filters")}
            confirmTitle={t("console.common.apply")}
            cancelTitle={t("console.common.cancel")}
            buttonElement={
                <button
                    data-modal-target="static-modal"
                    data-modal-toggle="static-modal"
                    className="flex items-center text-white  bg-light-bg-300 dark:bg-dark-bg-300 hover:bg-light-bg-300 dark:hover:bg-dark-bg-300 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-4 py-2.5 text-center max-h-10"
                    type="button"
                    title={t("console.common.filters")}
                    onClick={() => setIsVisible(true)}
                >
                    <FilterIconSVG className="w-[20px] fill-white" />
                </button>
            }
            confirmHandler={confirmHandler}
            closeHandler={closeHandler}
            onOpen={onOpenHandler}
            additionalWrapClassName="w-[450px]"
        >
            <div className="flex flex-col gap-3">
                {Object.keys(fieldsMeta).map((key) => {
                    const meta = fieldsMeta[key as keyof T];
                    if (!meta) return null;
                    const name = key as keyof T & string;

                    if (meta.type === "text" || meta.type === "number") {
                        return (
                            <CustomInput
                                className="text"
                                key={name}
                                type={meta.type}
                                name={name}
                                value={form.values[name] as string}
                                placeholderKey={meta.placeholderKey}
                                errorKey={form.errors[name] as string}
                                onChange={form.handleChange}
                            />
                        );
                    }

                    if (meta.type === "select") {
                        return (
                            <select
                                key={name}
                                name={name}
                                value={(form.values as any)[name] ?? ""}
                                onChange={form.handleChange}
                                className="border rounded p-2"
                            >
                                <option value="">{meta.placeholderKey}</option>
                                {meta.selectOptions?.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.labelKey}
                                    </option>
                                ))}
                            </select>
                        );
                    }

                    if (meta.type === "checkbox") {
                        return (
                            <CustomToggle
                                key={name}
                                labelKey={meta.placeholderKey}
                                name={name}
                                value={(form.values[name] as boolean) || false}
                                onChange={form.handleChange}
                                errorKey={form.errors[name] as string}
                            />
                        );
                    }

                    return null;
                })}
            </div>
        </BaseModal>
    );
}
